import { Navigate, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { Button, ListGroup, ListRow, useConfirm } from "@/ui";
import { useCancelPlayRequest, usePlayState, type Game } from "@/features/play/api";
import { BackHeader, Card, ErrorBox, Muted } from "@/features/play/parts";
import { describeMode, describeWhen, formatTime } from "@/features/play/format";
import { SearchRadiusMap } from "@/features/play/SearchRadiusMap";

/** «Играть»: the current search and my upcoming games from matchmaking. */
export function PlayPage() {
  const navigate = useNavigate();
  const state = usePlayState();
  const cancel = useCancelPlayRequest();
  const confirm = useConfirm();

  const req = state.data?.request ?? null;
  const games = state.data?.games ?? [];

  // Nothing to do here while a found game is being agreed — the board is
  // the screen that matters (it has «Мне не подходит» too).
  if (req?.status === "matched" && req.game_id) {
    return <Navigate to={`/play/game/${req.game_id}`} replace />;
  }

  const onCancel = async () => {
    if (!req) return;
    const ok = await confirm({
      title: "Остановить поиск?",
      description:
        req.status === "matched"
          ? "Ты выйдешь и из найденной игры — остальным сообщим."
          : "Новых соперников искать не будем.",
      confirmLabel: "Остановить",
      tone: "danger",
    });
    if (ok) cancel.mutate(req.id);
  };

  return (
    <div style={{ paddingBottom: 24 }}>
      <BackHeader title="Играть" onBack={() => navigate("/")} />

      {state.isLoading ? (
        <div className="body" style={{ padding: 16 }}>Загрузка…</div>
      ) : req ? (
        <Card accent>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {req.status === "searching" && <Spinner />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="body" style={{ fontWeight: 600 }}>
                {req.status === "searching" ? "Ищем, с кем сыграть" : "Нашли! Согласуйте игру"}
              </div>
              <Muted>
                {req.spots.map((s) => s.name).join(", ")} · {describeWhen(req)} · {describeMode(req.mode)}
              </Muted>
            </div>
          </div>
          {req.status === "searching" && (
            <>
              <SearchRadiusMap request={req} />
              <Muted>Можно закрыть приложение — когда найдём, напишем в Telegram.</Muted>
            </>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {req.status === "matched" && req.game_id && (
              <Button fullWidth size="sm" onClick={() => navigate(`/play/game/${req.game_id}`)}>
                Открыть доску
              </Button>
            )}
            <Button fullWidth size="sm" variant="secondary" onClick={onCancel} disabled={cancel.isPending}>
              Остановить поиск
            </Button>
          </div>
        </Card>
      ) : (
        <div style={{ padding: "12px 16px" }}>
          <Button fullWidth onClick={() => navigate("/play/search")}>Найти соперника</Button>
        </div>
      )}

      <ErrorBox error={cancel.error} />

      {games.length > 0 && (
        <>
          <div className="section-header" style={{ padding: "18px 16px 8px" }}>Мои игры</div>
          <ListGroup>
            {games.map((g) => (
              <ListRow
                key={g.id}
                title={gameTitle(g)}
                subtitle={gameSubtitle(g)}
                trailing={<ChevronRight size={18} color="var(--fg3)" />}
                onClick={() => navigate(`/play/game/${g.id}`)}
              />
            ))}
          </ListGroup>
        </>
      )}
    </div>
  );
}

function gameTitle(g: Game): string {
  if (g.my_status === "invited") return "Зовут в игру рядом";
  if (g.status === "scheduled" && g.spot && g.scheduled_at) {
    return `${g.spot.name} · ${formatTime(g.scheduled_at)}`;
  }
  return "Согласование спота и времени";
}

function gameSubtitle(g: Game): string {
  const n = g.participants.filter((p) => p.status === "active").length;
  const who = g.mode === "rated" ? "Рейтинг · 1×1" : `${n} чел.`;
  if (g.status === "scheduled") return `Игра назначена · ${who}`;
  return `${who} · выбери спот и время`;
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 28,
        height: 28,
        flex: "none",
        borderRadius: "50%",
        border: "3px solid var(--separator)",
        borderTopColor: "var(--brand-orange)",
        animation: "sp-spin 1.2s linear infinite",
      }}
    />
  );
}
